/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';

@Component({
    selector: 'app-lb-accordion-group',
    styleUrls: ['./accordion.component.scss'],
    template: `
        <div class="accordion">
            <div
                class="click-wrapper {{titleAlignment}}"
                [class.arrow-next-to-title]="arrowNextToTitle"
                [class.disabled]="!enableToggle"
                [style.height]="titleWrapperHeight === 'auto' ? titleWrapperHeight : titleWrapperHeight + 'px'"
                (click)="toggleAccordion()">
                <div class="title-wrapper" [class.disabled]="!enableToggle" [class.full-width]="fullWidth">
                    <div *ngIf="showTitle" class="header" [class.clickable]="showCursor">
                        <span class="header-text" [innerHTML]="title"></span>
                        <img
                            *ngIf="showComplete"
                            class="check"
                            ngSrc="../../../../assets/shared/icons/check.svg"
                            alt="step complete"
                            height="21"
                            width="21"/>
                    </div>
                    <div *ngIf="showArrow" class="arrow" [class.active]="open" [innerHTML]="arrow">
                    </div>
                </div>
            </div>
            <div
                [hidden]="hideContentWhenEmpty"
                class="accordion-content"
                (@openClose.start)="showHideScrollbar($event)"
                (@openClose.done)="showHideScrollbar($event)"
                [class.scrollBar]="animationComplete"
                [@openClose]="open ? 'open' : 'closed'">
                <ng-content></ng-content>
            </div>
        </div>
    `,
    animations: [
        trigger('openClose', [
            state('open',
                style({
                    'max-height': '1000px'
                })
            ),
            state('closed',
                style({
                    'max-height': '0',
                })
            ),
            transition('open => closed', [
                animate('0.5s ease-out', keyframes([
                    style({ 'max-height': '1000px', offset: 0 }),
                    style({ 'max-height': '0', offset: 1 })
                ])),
            ]),
            transition('closed => open', [
                animate('0.5s ease-in', keyframes([
                    style({ 'max-height': '0', offset: 0 }),
                    style({ 'max-height': '1000px', offset: 1 }),
                ])),
            ])
        ])
    ]
})

export class AccordionGroupComponent {
    /**
     * A boolean to determine if the accordion is open or not.
     */
    @Input() public open: boolean = false;

    /**
     * Force close
     */
    @Input() set forceClosed(state: boolean) {
        if (state) {
            this.open = false;
        }
    }

    /**
     * A boolean ot determine if the toggle is enabled.
     */
    @Input() public enableToggle: boolean = true;

    /**
     * To store the title.
     */
    @Input() public title: string;

    /**
     * To store the title.
     */
    @Input() public titleAlignment: 'left' | 'center' | 'right' = 'left';

    /**
     * A boolean to determine if the title should be shown or not.
     */
    @Input() public showTitle: boolean = true;

    /**
     * A boolean to determine if the arrow should be shown or not.
     */
    @Input() public showArrow: boolean = true;

    /**
     * Arrow used for toggle.
     */
    @Input() public arrow = '<img src="assets/shared/icons/chevron-right-square.svg" alt="chevron right icon">';

    /**
     * A boolean to determine if the tick icon should be shown or not.
     */
    @Input() public showComplete: boolean = false;

    /**
     * A boolean to determine if the arrow next title should be shown or not.
     */
    @Input() public arrowNextToTitle: boolean = false;

    /**
     * A boolean to determine if the title should be clickable.
     */
    @Input() public showCursor: boolean = true;

    /**
     * A boolean to determine the width of the toggle.
     */
    @Input() public fullWidth: boolean = false;

    /**
     * Title wrapper height
     */
    @Input() public titleWrapperHeight: 'auto' | number = 'auto';

    /**
     * Parent component can hide the accordion if it is empty.
     */
    @Input() public hideContentWhenEmpty: boolean = false;

    /**
     * An event emitter to output the toggle.
     */
    @Output() public toggleEvent: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Animation complete state
     */
    public animationComplete: boolean = false;

    /**
     * Toggles the accordion open state.
     */
    public toggleAccordion() {
        if (this.enableToggle) {
            this.open = !this.open;
            this.toggleEvent.next(this.open);
        }
    }

    /**
     * Show hide scrollbar after/before animation.
     * @param $event
     */
    public showHideScrollbar($event: any): void {
        if ($event.fromState !== 'void') {
            if ($event.phaseName === 'done' && $event.toState === 'open') {
                this.animationComplete = true;
            }
            if ($event.phaseName === 'start' && $event.toState === 'closed') {
                this.animationComplete = false;
            }
        }
    }
}
